import {
  FidelityCategory,
  FidelityContract,
  FidelityStatus,
} from '../interfaces/fidelity';

export type ContractScenario = 'current_only' | 'next_only' | 'both' | 'idle';

export interface FidelityContractData {
  stato?: FidelityStatus;
  scadenzaContratto?: string | null;
  contratto?: FidelityContract | null;
  annoUltimaApprovazione?: number | null;
  categorie?: FidelityCategory[] | null;
  ragioneSociale?: string;
  nome?: string;
  email?: string;
  telefono?: string;
  indirizzo?: string;
  citta?: string;
  cap?: string;
  provincia?: string;
  sezione?: { data?: { id?: number | null } | null } | number | null;
}

const RENEWAL_MONTH = 10;

export const isAfterRenewalDate = (date: Date = new Date()) =>
  date.getMonth() >= RENEWAL_MONTH;

export const extractContractYears = (
  contratto?: FidelityContract | null
): number[] => {
  if (!contratto?.years) return [];
  return Object.keys(contratto.years)
    .map((year) => Number(year))
    .filter((year) => Number.isFinite(year))
    .sort((a, b) => a - b);
};

export const extractPendingYears = (
  contratto?: FidelityContract | null,
  annoUltimaApprovazione?: number | null
): number[] => {
  const allYears = extractContractYears(contratto);
  if (!annoUltimaApprovazione) return allYears;
  return allYears.filter((year) => year > annoUltimaApprovazione);
};

export const extractApprovedYears = (
  contratto?: FidelityContract | null,
  annoUltimaApprovazione?: number | null
): number[] => {
  const allYears = extractContractYears(contratto);
  if (!annoUltimaApprovazione) return [];
  return allYears.filter((year) => year <= annoUltimaApprovazione);
};

export const getAvailableContractYears = (
  approvedYears: number[],
  pendingYears: number[],
  date: Date = new Date()
): number[] => {
  const currentYear = date.getFullYear();
  const nextYear = currentYear + 1;
  const afterRenewal = isAfterRenewalDate(date);

  const hasApprovedCurrentYear = approvedYears.includes(currentYear);
  const hasApprovedNextYear = approvedYears.includes(nextYear);
  const hasPendingCurrentYear = pendingYears.includes(currentYear);
  const hasPendingNextYear = pendingYears.includes(nextYear);

  const available: number[] = [];

  if (!hasApprovedCurrentYear && !hasPendingCurrentYear) {
    available.push(currentYear);
  }

  if (afterRenewal && !hasApprovedNextYear && !hasPendingNextYear) {
    available.push(nextYear);
  }

  return available;
};

export const getContractScenario = (
  approvedYears: number[],
  date: Date = new Date()
): ContractScenario => {
  const currentYear = date.getFullYear();
  const nextYear = currentYear + 1;
  const afterRenewal = isAfterRenewalDate(date);

  const hasApprovedCurrentYear = approvedYears.includes(currentYear);
  const hasApprovedNextYear = approvedYears.includes(nextYear);

  if (!afterRenewal) {
    if (hasApprovedCurrentYear) return 'idle';
    return 'current_only';
  }

  if (hasApprovedCurrentYear && hasApprovedNextYear) return 'idle';
  if (hasApprovedCurrentYear && !hasApprovedNextYear) return 'next_only';
  if (!hasApprovedCurrentYear && hasApprovedNextYear) return 'current_only';
  return 'both';
};

export const getEffectiveStatus = (
  status?: FidelityStatus,
  scadenzaContratto?: string | null,
  date: Date = new Date()
): FidelityStatus => {
  const currentStatus = status || FidelityStatus.BOZZA;
  if (currentStatus === FidelityStatus.APPROVATO && scadenzaContratto) {
    const expiry = new Date(scadenzaContratto);
    if (!Number.isNaN(expiry.getTime()) && expiry.getTime() < date.getTime()) {
      return FidelityStatus.SCADUTO;
    }
  }
  return currentStatus;
};

export const hasProfileCompleted = (
  fidelity?: FidelityContractData | null
): boolean => {
  if (!fidelity) return false;
  const requiredStrings = [
    fidelity.ragioneSociale,
    fidelity.nome,
    fidelity.email,
    fidelity.telefono,
    fidelity.indirizzo,
    fidelity.citta,
    fidelity.cap,
    fidelity.provincia,
  ];
  const hasAll = requiredStrings.every(
    (value) => typeof value === 'string' && value.trim().length > 0
  );
  const sezioneId =
    typeof fidelity.sezione === 'number'
      ? fidelity.sezione
      : fidelity.sezione?.data?.id;

  return hasAll && Boolean(sezioneId);
};

export const hasCategories = (fidelity?: FidelityContractData | null) =>
  Boolean(fidelity?.categorie && fidelity.categorie.length > 0);

export const getActiveContractYear = (
  contratto?: FidelityContract | null,
  annoUltimaApprovazione?: number | null,
  date: Date = new Date()
): number | null => {
  const approvedYears = extractApprovedYears(contratto, annoUltimaApprovazione);
  const resolvedApprovedYears =
    approvedYears.length === 0 && annoUltimaApprovazione
      ? [annoUltimaApprovazione]
      : approvedYears;
  if (resolvedApprovedYears.length === 0) return null;
  const currentYear = date.getFullYear();
  const nextYear = currentYear + 1;

  if (isAfterRenewalDate(date) && resolvedApprovedYears.includes(nextYear)) {
    return nextYear;
  }

  if (resolvedApprovedYears.includes(currentYear)) {
    return currentYear;
  }

  return null;
};
